/* eslint-disable no-param-reassign, no-use-before-define, @typescript-eslint/no-use-before-define, no-nested-ternary, no-continue */
/**
 * Multiconexão: five NoPing routes to the game server at once, the fastest one wins.
 *
 * Imperative port of the design's `initMC` (it was an iframe; now it runs on the card's own markup). The
 * five rows are slots (r1-r5) filled for where the visitor is: the game server is the country's hub, the
 * routes are the visitor's city, the hub and the nearest cities. The winner sits at 2-5 ms, the rest a
 * little above, the direct line ("Sem NoPing") swings between 180 and 250 ms; every half second the
 * figures move within their bands, every ~5 s a scenario can hand the lead over. Packets run on WAAPI
 * along wires drawn from the measured boxes. Everything stops off screen, in a hidden tab, when paused
 * and under reduced motion. `start` returns the teardown.
 */
import type { GeoData, RawGeoAnswer } from './geo';
import { escapeHtml, foldText, forcedGeoParams, requestVisitorGeo } from './geo';
import type { Translate } from './runtime';
import { createRuntime, one, restartClass, round1 } from './runtime';

export interface EngineOptions {
  geo: GeoData;
  translate: Translate;
  cityName: (name: string) => string;
  countryName: (countryCode: string) => string;
  joinList: (items: string[]) => string;
}

interface Place {
  city: string;
  code: string;
  lat: number;
  lon: number;
  cc: string;
}

interface Scenario {
  bad: number;
  [routeId: string]: number;
}

interface Profile {
  key: string;
  cc: string;
  hub: Place;
  user: Place | null;
  routes: Place[];
  scenarios: Scenario[];
}

interface Box {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface MotionGeo {
  head: string;
  rest: string;
  w: number;
  parks: number[];
}

interface PacketSlot {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface PacketSet {
  set: HTMLDivElement | null;
  packets: PacketSlot[];
  geo: MotionGeo | null;
  frames: Keyframe[] | null;
  framesHide?: Keyframe[];
  framesOpen?: Keyframe[];
  parkIdx?: number[];
  lat: number;
}

interface Route extends PacketSet {
  id: string;
  city: string;
  country: string;
  code: string;
  shown: number;
  hist: number[];
  el: HTMLButtonElement;
  msEl: HTMLElement;
  msWrap: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement;
  spFill: SVGPolygonElement;
  spDot: SVGCircleElement;
  tw: number;
  rect?: Box;
  g?: SVGGElement;
  pathBase?: SVGPathElement;
  pathGlow?: SVGPathElement;
  pathHot?: SVGPathElement;
  pathSweep?: SVGPathElement;
  pathKnock?: SVGPathElement;
  portIn?: SVGCircleElement;
  portOut?: SVGCircleElement;
}

interface WinnerResult {
  best: Route;
  prev: Route | null;
  changed: boolean;
}

interface StageGeometry {
  mobile: boolean;
  dPc: number;
  dGame: number;
  a: { x: number; y: number };
  b: { x: number; y: number };
  ra: { x: number; y: number };
  rb: { x: number; y: number };
  routes: { base: string; full: string; l: number; r: number; cy: number; geo: MotionGeo }[];
  direct: { wire: string; head: string; tail: string; l: number; r: number; cy: number; geo: MotionGeo };
  trunks: [string, string];
}

const NS = 'http://www.w3.org/2000/svg';
const FALLBACK_COUNTRY = 'US';
const LAT_MIN = 2;
const BAD_LO = 180;
const BAD_HI = 250;
const HIST_N = 8;
const BASE_MS = 1600;
const SP_W = 52;
const SP_H = 18;
const SP_LO = 0;
const SP_HI = 40;
const SP_K = 2.2;
const STUB_V = 14;
const MASK_PAD = 48;
const NODE_EDGE_PAD = 4;
const LIVE_MS = 500;
const LOOP_MS = 5200;

function randomInt(low: number, high: number): number {
  return low + Math.floor(Math.random() * (high - low + 1));
}

function toNumber(value: unknown): number | null {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function kilometres(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const radians = Math.PI / 180;
  const dLat = (to.lat - from.lat) * radians;
  const dLon = (to.lon - from.lon) * radians;
  const haversine = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(from.lat * radians) * Math.cos(to.lat * radians) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 12742 * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

function box(rect: DOMRect, stage: DOMRect): Box {
  return { l: rect.left - stage.left, t: rect.top - stage.top, r: rect.right - stage.left, b: rect.bottom - stage.top, w: rect.width, h: rect.height, cx: rect.left - stage.left + rect.width / 2, cy: rect.top - stage.top + rect.height / 2 };
}

function shiftXY(rect: Box, dx: number, dy: number): Box {
  return { l: rect.l + dx, r: rect.r + dx, t: rect.t + dy, b: rect.b + dy, w: rect.w, h: rect.h, cx: rect.cx + dx, cy: rect.cy + dy };
}

function shiftBox(rect: Box, dy: number): Box {
  return { ...rect, t: rect.t + dy, b: rect.b + dy, cy: rect.cy + dy };
}

/**
 * Clamps a desktop node's vertical shift (`--mc-dy`, which moves the label and the icon together) so the
 * label stacked above the icon never rises past `limitTop` (the stage's padding edge, inside the card that
 * clips) and the icon never sinks past `limitBottom`. When both cannot hold, the label wins: a clipped
 * label is what SCRUM-2138 reported. All values share the wires' coordinate space.
 */
export function clampNodeShift(wanted: number, labelTop: number, iconBottom: number, limitTop: number, limitBottom: number): number {
  const lowestShift = limitTop + NODE_EDGE_PAD - labelTop;
  const highestShift = limitBottom - NODE_EDGE_PAD - iconBottom;
  return Math.max(lowestShift, Math.min(highestShift, wanted));
}

function sideOffset(rect: Box, radius: number): number {
  return Math.max(10, Math.min(22, rect.h / 2 - radius - 8));
}

function fanA(x1: number, y1: number, x2: number, y2: number): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return `C${round1(x1 + dx * 0.42)},${round1(y1 + dy * 0.34)} ${round1(x2 - dx * 0.42)},${round1(y2)} ${round1(x2)},${round1(y2)}`;
}

function fanB(x1: number, y1: number, x2: number, y2: number): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return `C${round1(x1 + dx * 0.42)},${round1(y1)} ${round1(x2 - dx * 0.42)},${round1(y2 - dy * 0.34)} ${round1(x2)},${round1(y2)}`;
}

function stubIn(tx: number, cy: number, cl: number): string {
  const run = Math.max(6, cl - tx);
  return `C${round1(tx)},${round1(cy - STUB_V * 0.45)} ${round1(tx + run * 0.5)},${round1(cy)} ${round1(cl)},${round1(cy)}`;
}

function stubOut(cr: number, cy: number, rx: number): string {
  const run = Math.max(6, rx - cr);
  return `C${round1(cr + run * 0.5)},${round1(cy)} ${round1(rx)},${round1(cy + STUB_V * 0.55)} ${round1(rx)},${round1(cy + STUB_V)}`;
}

function springOut(progress: number): number {
  const overshoot = 0.9;
  const offset = progress - 1;
  return 1 + (overshoot + 1) * offset * offset * offset + overshoot * offset * offset;
}

/** cubic-bezier(.16, 1, .3, 1), the kit's --ease-out, for the number ticks. */
function bezier(p1x: number, p1y: number, p2x: number, p2y: number): (x: number) => number {
  const coefficientA = (first: number, second: number) => 1 - 3 * second + 3 * first;
  const coefficientB = (first: number, second: number) => 3 * second - 6 * first;
  const coefficientC = (first: number) => 3 * first;
  const calc = (time: number, first: number, second: number) => ((coefficientA(first, second) * time + coefficientB(first, second)) * time + coefficientC(first)) * time;
  const slope = (time: number, first: number, second: number) => 3 * coefficientA(first, second) * time * time + 2 * coefficientB(first, second) * time + coefficientC(first);
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let time = x;
    for (let iteration = 0; iteration < 6; iteration += 1) {
      const currentSlope = slope(time, p1x, p2x);
      if (Math.abs(currentSlope) < 1e-6) break;
      time -= (calc(time, p1x, p2x) - x) / currentSlope;
    }
    return calc(time, p1y, p2y);
  };
}

const easeOut = bezier(0.16, 1, 0.3, 1);

export function startMulticonexao(root: HTMLElement, options: EngineOptions): () => void {
  const { geo, translate, cityName, countryName, joinList } = options;
  const t = (key: string, values?: Record<string, unknown>) => translate(`mc.${key}`, { ...values, interpolation: { escapeValue: false } });

  const runtime = createRuntime();
  const { later, every, clearEvery, nextFrame, cancelFrame, listen } = runtime;
  const alive = () => runtime.isAlive();

  root.classList.add('is-js');
  const stage = one<HTMLElement>(root, '[data-stage]');
  const svg = one<SVGSVGElement>(root, '[data-wires]');
  const packetLayer = one<HTMLElement>(root, '[data-packets]');
  const list = one<HTMLElement>(root, '[data-routes]');
  const pcNode = one<HTMLElement>(root, '[data-node="pc"]');
  const gameNode = one<HTMLElement>(root, '[data-node="game"]');
  const directEl = one<HTMLElement>(root, '[data-direct]');
  const directMs = one<HTMLElement>(root, '[data-direct-ms]');
  const statusText = one<HTMLElement>(root, '[data-status-text]');
  const liveEl = one<HTMLElement>(root, '[data-live]');
  const bridge = one<HTMLElement>(root, '[data-bridge]');
  const retestButton = one<HTMLButtonElement>(root, '[data-retest]');
  const retestLabel = one<HTMLElement>(retestButton, '[data-lbl]');
  const pauseButton = one<HTMLButtonElement>(root, '[data-pause]');
  const clearButton = one<HTMLButtonElement>(root, '[data-clear]');
  const hintText = one<HTMLElement>(root, '[data-hint]');
  const proofEl = one<HTMLElement>(root, '[data-proof]');
  const proofSr = one<HTMLElement>(root, '[data-proof-sr]');
  const proofGood = one<HTMLElement>(root, '[data-proof-good]');
  const proofPct = one<HTMLElement>(root, '[data-proof-pct]');
  const proofFill = root.querySelector<HTMLElement>('[data-proof-fill]');
  const pcPlace = root.querySelector<HTMLElement>('[data-pc-place]');
  const gamePlace = root.querySelector<HTMLElement>('[data-game-place]');
  const resultBad = root.querySelector<HTMLElement>('[data-result-bad]');
  const proofBad = root.querySelector<HTMLElement>('[data-proof-bad]');
  const resultGood = root.querySelector<HTMLElement>('[data-result-good]');
  const resultPct = root.querySelector<HTMLElement>('[data-result-pct]');
  const descEl = root.querySelector<HTMLElement>('[data-mc-desc]');
  const wrapEl = one<HTMLElement>(root, '.mc-routes-wrap');
  const pcIcon = one<HTMLElement>(pcNode, '.mc-node-icon');
  const gameIcon = one<HTMLElement>(gameNode, '.mc-node-icon');
  const pcLabel = one<HTMLElement>(pcNode, '.mc-node-label');
  const gameLabel = one<HTMLElement>(gameNode, '.mc-node-label');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsMotion = typeof packetLayer.animate === 'function';
  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mc-mobile').trim() === '1';

  /* ------------------------------------------------ routes for the visitor */
  const COUNTRIES = geo.countries;
  const makeCity = (countryCode: string, tuple: [string, string, number, number]): Place => ({ city: tuple[0], code: tuple[1], lat: tuple[2], lon: tuple[3], cc: countryCode });
  const findCity = (countryCode: string, name: unknown): Place | null => {
    const cities = COUNTRIES[countryCode]?.[2] ?? [];
    let folded = foldText(name);
    const alias = geo.aliases[folded];
    if (alias) folded = foldText(alias);
    if (!folded) return null;
    const match = cities.find((tuple) => foldText(tuple[0]) === folded);
    return match ? makeCity(countryCode, match) : null;
  };
  const codeOf = (name: string) => `${foldText(name).replace(/ /g, '').toUpperCase()}XXX`.slice(0, 3);
  const normalizeGeo = (data: unknown): (RawGeoAnswer & { country: string; lat: number | null; lon: number | null }) | null => {
    if (!data || typeof data !== 'object') return null;
    const record = data as Record<string, unknown>;
    const countryField = record.country;
    const code = record.country_code ?? record.countryCode ?? (countryField && typeof countryField === 'object' ? (countryField as Record<string, unknown>).code : countryField);
    if (!code || String(code).length !== 2) return null;
    return {
      country: String(code).toUpperCase(),
      region: String(record.region_code ?? record.regionCode ?? record.region ?? record.state ?? ''),
      city: String(record.city ?? ''),
      lat: toNumber(record.lat ?? record.latitude),
      lon: toNumber(record.lon ?? record.lng ?? record.longitude),
    };
  };
  const forced = normalizeGeo(forcedGeoParams(window.location.search));

  const profileFor = (answer: ReturnType<typeof normalizeGeo>): Profile => {
    const target = answer && COUNTRIES[answer.country] ? answer : { country: FALLBACK_COUNTRY, region: '', city: '', lat: null, lon: null };
    const countryCode = target.country;
    const meta = COUNTRIES[countryCode] ?? COUNTRIES[FALLBACK_COUNTRY];
    if (!meta) throw new Error('Multiconexão: fallback country missing from geo data');
    const hubTuple = meta[2].find((tuple) => tuple[0] === meta[1]) ?? meta[2][0];
    if (!hubTuple) throw new Error('Multiconexão: country without cities');
    const hub = makeCity(countryCode, hubTuple);
    let user: Place | null = findCity(countryCode, target.city);
    if (!user && target.city) {
      const regionCity = findCity(countryCode, (geo.regions[countryCode] ?? {})[String(target.region).toUpperCase()]);
      const at = target.lat !== null && target.lon !== null ? { lat: target.lat, lon: target.lon } : (regionCity ?? hub);
      user = { city: String(target.city), code: codeOf(String(target.city)), lat: at.lat, lon: at.lon, cc: countryCode };
    }
    const from = user ?? hub;
    const routes: Place[] = [];
    if (user && foldText(user.city) !== foldText(hub.city)) routes.push(user);
    routes.push(hub);
    const taken = (place: Place) => routes.some((route) => foldText(route.city) === foldText(place.city));
    const nearest = (pool: Place[]) => pool.filter((place) => !taken(place)).sort((first, second) => kilometres(from, first) - kilometres(from, second));
    const own = nearest(meta[2].map((tuple) => makeCity(countryCode, tuple)));
    while (routes.length < 5 && own.length) routes.push(own.shift() as Place);
    if (routes.length < 5) {
      const everywhere: Place[] = [];
      Object.keys(COUNTRIES).forEach((otherCode) => {
        if (otherCode === countryCode) return;
        COUNTRIES[otherCode]?.[2].forEach((tuple) => everywhere.push(makeCity(otherCode, tuple)));
      });
      const sorted = nearest(everywhere);
      while (routes.length < 5 && sorted.length) routes.push(sorted.shift() as Place);
    }
    /* latency: how far each city bends the path from the visitor to the hub (fibre: ~1 ms per 100 km) */
    const direct = kilometres(from, hub);
    const rank = routes.map((place, index) => ({ index, detour: kilometres(from, place) + kilometres(place, hub) - direct })).sort((first, second) => first.detour - second.detour || first.index - second.index);
    const base: number[] = [];
    rank.forEach((entry, position) => {
      base[entry.index] = position < 2 ? 0 : Math.min(40, 6 + position + Math.round(entry.detour / 100));
    });
    const first = rank[0]?.index ?? 0;
    const second = rank[1]?.index ?? 1;
    const scenarios: Scenario[] = [];
    for (let step = 0; step < 5; step += 1) {
      const row: Scenario = { bad: randomInt(BAD_LO, BAD_HI) };
      routes.forEach((_place, index) => {
        const id = `r${index + 1}`;
        if (index === first) row[id] = step % 2 ? randomInt(7, 10) : randomInt(2, 3);
        else if (index === second) row[id] = step % 2 ? randomInt(2, 4) : randomInt(4, 5);
        else row[id] = (base[index] ?? 0) + randomInt(0, 2);
      });
      scenarios.push(row);
    }
    return { key: `${countryCode}|${foldText(user ? user.city : '')}`, cc: countryCode, hub, user, routes, scenarios };
  };

  /* ------------------------------------------------------------------ state */
  let place: Profile = profileFor(forced);
  let { scenarios } = place;
  let badMs = scenarios[0]?.bad ?? 208;
  let scenarioIndex = 0;
  let winnerId: string | null = null;
  let focusId: string | null = null;
  let pinned: string | null = null;
  let testing = false;
  let visible = true;
  let paused = false;
  let boost = 1;
  let running = false;
  let built = false;
  const direct: PacketSet = { set: null, packets: [], geo: null, frames: null, lat: badMs };

  const scenarioValue = (scenario: Scenario | undefined, id: string) => scenario?.[id] ?? LAT_MIN;

  const routes: Route[] = ['r1', 'r2', 'r3', 'r4', 'r5'].map((id) => {
    const el = one<HTMLButtonElement>(list, `[data-route="${id}"]`);
    const lat = scenarioValue(scenarios[0], id);
    return {
      id,
      city: '',
      country: '',
      code: '',
      lat,
      shown: lat,
      hist: [],
      el,
      msEl: one<HTMLElement>(el, '[data-ms]'),
      msWrap: one<HTMLElement>(el, '.mc-route-ms'),
      deltaEl: one<HTMLElement>(el, '[data-delta]'),
      spLine: one<SVGPolylineElement>(el, '.mc-route-spark polyline'),
      spFill: one<SVGPolygonElement>(el, '.mc-route-spark .sp-fill'),
      spDot: one<SVGCircleElement>(el, '.mc-route-spark .sp-dot'),
      tw: 0,
      set: null,
      packets: [],
      geo: null,
      frames: null,
    };
  });
  const byId = (id: string | null) => routes.find((route) => route.id === id) ?? null;

  const applyProfile = (profile: Profile) => {
    place = profile;
    routes.forEach((route, index) => {
      const city = profile.routes[index];
      if (!city) return;
      route.city = cityName(city.city);
      route.country = countryName(city.cc);
      route.code = city.code;
    });
    scenarios = profile.scenarios;
    badMs = scenarios[0]?.bad ?? badMs;
  };
  applyProfile(place);

  /* ------------------------------------------------------------ sparklines */
  const sparkY = (lat: number) => SP_H - 1.5 - (SP_H - 3) * Math.max(0, Math.min(1, (lat - SP_LO) / (SP_HI - SP_LO)));
  const drawSpark = (route: Route) => {
    const count = route.hist.length;
    const mean = route.hist.reduce((sum, value) => sum + value, 0) / (count || 1);
    const base = sparkY(mean);
    const points = route.hist.map((value, index) => `${round1(1.5 + ((SP_W - 3) * index) / (HIST_N - 1))},${round1(Math.max(1.5, Math.min(SP_H - 1.5, base - (value - mean) * SP_K)))}`);
    route.spLine.setAttribute('points', points.join(' '));
    route.spFill.setAttribute('points', `1.5,${SP_H} ${points.join(' ')} ${round1(1.5 + ((SP_W - 3) * (count - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = (points[count - 1] ?? '0,0').split(',');
    route.spDot.setAttribute('cx', last[0] ?? '0');
    route.spDot.setAttribute('cy', last[1] ?? '0');
  };
  const pushReading = (route: Route) => {
    route.hist.push(route.lat);
    if (route.hist.length > HIST_N) route.hist.shift();
    drawSpark(route);
  };
  const seedHistory = (route: Route, amplitude: number) => {
    route.hist = [];
    const floor = route.lat <= LAT_MIN;
    for (let index = 0; index < HIST_N - 1; index += 1) route.hist.push(Math.max(LAT_MIN, route.lat + (floor ? 1 + Math.round(Math.random() * amplitude) : Math.round((Math.random() * 2 - 1) * amplitude))));
    route.hist.push(route.lat);
  };

  const setShown = (route: Route, value: number) => {
    route.shown = value;
    route.msEl.textContent = String(value);
  };
  const paintRow = (route: Route) => {
    one<HTMLElement>(route.el, '.mc-route-city').textContent = route.city;
    one<HTMLElement>(route.el, '.mc-route-country').textContent = route.country;
    one<HTMLElement>(route.el, '.mc-route-cc').textContent = route.code;
  };
  const paintPlace = () => {
    if (pcPlace) pcPlace.textContent = place.user ? cityName(place.user.city) : t('pc_place');
    if (gamePlace) gamePlace.textContent = t('game_place_in', { city: cityName(place.hub.city) });
    if (descEl) descEl.textContent = t('description', { routes: joinList(routes.map((route) => route.city)), hub: cityName(place.hub.city) });
  };
  const setBad = (value: number, amplitude: number) => {
    badMs = Math.max(BAD_LO, Math.min(BAD_HI, value + (amplitude ? randomInt(-6, 6) : 0)));
    direct.lat = badMs;
    if (proofBad) proofBad.textContent = String(badMs);
    if (resultBad) resultBad.textContent = String(badMs);
  };

  /* the host page can mirror these routes elsewhere (window.__npMcPlace + "np:mc-place" event) */
  const publishPlace = () => {
    try {
      // eslint-disable-next-line no-underscore-dangle -- the design's public mirror contract (window.__npMcPlace)
      (window as unknown as { __npMcPlace?: Profile }).__npMcPlace = place;
      window.dispatchEvent(new CustomEvent('np:mc-place', { detail: place }));
    } catch {
      // A host that forbids custom events simply does not get the mirror.
    }
  };

  routes.forEach((route) => {
    seedHistory(route, 1);
    drawSpark(route);
    setShown(route, route.lat);
    paintRow(route);
  });
  paintPlace();
  setBad(badMs, 0);
  publishPlace();

  /* ---------------------------------------------------------------- proof */
  const pctFor = (lat: number) => Math.max(0, Math.min(99, Math.round(((badMs - lat) / badMs) * 100)));
  const proof = { lat: -1, bad: -1 };
  const paintProof = (lat: number, pct: number) => {
    proofGood.textContent = String(lat);
    proofPct.textContent = String(pct);
    if (resultGood) resultGood.textContent = String(lat);
    if (resultPct) resultPct.textContent = String(pct);
  };
  const setProof = (lat: number) => {
    if (proof.lat === lat && proof.bad === badMs) return;
    proof.lat = lat;
    proof.bad = badMs;
    proofSr.textContent = t('proof_sr', { bad: badMs, good: lat, pct: pctFor(lat) });
    if (proofFill) proofFill.style.setProperty('--mc-fill', `${Math.round((lat / badMs) * 1000) / 10}%`);
  };
  const stMs = (route: Route) => `<span data-st-ms="${route.id}">${route.shown}</span>`;
  const stDelta = (route: Route, best: Route) => `<span data-st-delta="${route.id}">${Math.max(0, route.shown - best.shown)}</span>`;
  const bold = (text: string) => `<b>${escapeHtml(text)}</b>`;

  const syncDerived = () => {
    const winner = byId(winnerId);
    if (!winner) return;
    const winnerMs = winner.shown;
    routes.forEach((route) => {
      route.deltaEl.textContent = route === winner ? t('active_now') : t('delta_ms', { ms: Math.max(0, route.shown - winnerMs) });
    });
    directMs.textContent = String(badMs - winnerMs);
    paintProof(winnerMs, pctFor(winnerMs));
    statusText.querySelectorAll<HTMLElement>('[data-st-ms],[data-st-delta]').forEach((span) => {
      const msId = span.getAttribute('data-st-ms');
      if (msId) {
        const route = byId(msId);
        if (route) span.textContent = String(route.shown);
        return;
      }
      const route = byId(span.getAttribute('data-st-delta'));
      if (route) span.textContent = String(Math.max(0, route.shown - winnerMs));
    });
  };
  const rankOf = (route: Route) => 1 + routes.filter((other, index) => other !== route && (other.lat < route.lat || (other.lat === route.lat && index < routes.indexOf(route)))).length;
  const flip = (element: Element) => {
    if (reduced.matches) return;
    restartClass(element, 'is-flip');
  };
  const tickMs = (route: Route, from: number, to: number) => {
    if (route.tw) {
      cancelFrame(route.tw);
      route.tw = 0;
    }
    const badge = directMs.textContent;
    const land = () => {
      setShown(route, to);
      syncDerived();
      flip(route.msWrap);
      if (built && directMs.textContent !== badge && directMs.parentElement) flip(directMs.parentElement);
    };
    if (reduced.matches || Math.abs(to - from) < 3) {
      land();
      return;
    }
    const startedAt = performance.now();
    const duration = 560;
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      if (progress < 1) {
        setShown(route, Math.round(from + (to - from) * easeOut(progress)));
        syncDerived();
        route.tw = nextFrame(step);
        return;
      }
      route.tw = 0;
      land();
    };
    route.tw = nextFrame(step);
  };
  const labelFor = (route: Route, best: Route) => (route.id === best.id ? t('route_label_winner', { city: route.city, country: route.country, ms: route.lat }) : t('route_label', { city: route.city, country: route.country, ms: route.lat, delta: route.lat - best.lat }));

  /* ------------------------------------------------------------- the stage */
  const ports: Record<'pc' | 'game', SVGCircleElement | null> = { pc: null, game: null };
  let measure: SVGPathElement | null = null;
  let mask: SVGMaskElement | null = null;
  let maskBg: SVGRectElement | null = null;
  let directPath: SVGPathElement | null = null;
  let directTail: SVGPathElement | null = null;
  const directPorts: SVGCircleElement[] = [];
  const trunks: SVGPathElement[] = [];

  const svgEl = <K extends keyof SVGElementTagNameMap>(tag: K, attributes: Record<string, string> = {}) => {
    const element = document.createElementNS(NS, tag);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    return element;
  };
  const makePackets = (count: number, className: string) => {
    const set = document.createElement('div');
    set.className = `pk-set${className ? ` ${className}` : ''}`;
    const packets: PacketSlot[] = [];
    for (let index = 0; index < count; index += 1) {
      const element = document.createElement('div');
      element.className = 'pk';
      set.appendChild(element);
      packets.push({ el: element, phase: (index + 0.15) / count, anim: null });
    }
    packetLayer.appendChild(set);
    return { set, packets };
  };

  const buildStage = () => {
    if (built) return;
    built = true;
    const portLayer = svgEl('g');
    (['pc', 'game'] as const).forEach((key) => {
      const circle = svgEl('circle', { r: '4', class: `w-port${winnerId ? ' is-hot' : ''}` });
      portLayer.appendChild(circle);
      ports[key] = circle;
    });
    measure = svgEl('path', { fill: 'none', stroke: 'none' });
    svg.appendChild(measure);
    const defs = svgEl('defs');
    mask = svgEl('mask', { id: 'mc-knock', maskUnits: 'userSpaceOnUse', x: '0', y: '0', width: '100%', height: '100%' });
    maskBg = svgEl('rect', { width: '100%', height: '100%', fill: '#fff' });
    mask.appendChild(maskBg);
    defs.appendChild(mask);
    svg.appendChild(defs);
    const directGroup = svgEl('g', { class: 'w-directwrap' });
    const directInner = svgEl('g', { mask: 'url(#mc-knock)' });
    directPath = svgEl('path', { class: 'w-direct' });
    directTail = svgEl('path', { class: 'w-direct' });
    directInner.appendChild(directPath);
    directInner.appendChild(directTail);
    directGroup.appendChild(directInner);
    for (let index = 0; index < 4; index += 1) {
      const port = svgEl('circle', { r: index < 2 ? '2.5' : '4', class: `w-dport${index < 2 ? '' : ' w-dport--end'}` });
      directGroup.appendChild(port);
      directPorts.push(port);
    }
    svg.appendChild(directGroup);
    const directPackets = makePackets(2, 'is-direct');
    direct.set = directPackets.set;
    direct.packets = directPackets.packets;
    const trunkGroup = svgEl('g', { class: 'w-trunks' });
    for (let index = 0; index < 2; index += 1) {
      const trunk = svgEl('path', { class: 'w-base w-trunk' });
      trunkGroup.appendChild(trunk);
      trunks.push(trunk);
    }
    svg.appendChild(trunkGroup);
    routes.forEach((route) => {
      const group = svgEl('g', { 'data-wire': route.id });
      const base = svgEl('path', { class: 'w-base' });
      const glow = svgEl('path', { class: 'w-glow' });
      const hotWrap = svgEl('g', { class: 'w-hotwrap' });
      const hot = svgEl('path', { class: 'w-hot' });
      const sweep = svgEl('path', { class: 'w-sweep' });
      hotWrap.appendChild(hot);
      const portIn = svgEl('circle', { r: '2.75', class: 'w-rport' });
      const portOut = svgEl('circle', { r: '2.75', class: 'w-rport' });
      [base, glow, hotWrap, sweep, portIn, portOut].forEach((child) => group.appendChild(child));
      svg.appendChild(group);
      const knock = svgEl('path', { class: 'w-knock' });
      mask?.appendChild(knock);
      Object.assign(route, { g: group, pathBase: base, pathGlow: glow, pathHot: hot, pathSweep: sweep, portIn, portOut, pathKnock: knock });
      const packets = makePackets(3, '');
      route.set = packets.set;
      route.packets = packets.packets;
      if (route.id === winnerId) {
        group.classList.add('is-winner');
        route.set.classList.add('is-winner');
      }
      if (route.id === focusId) {
        group.classList.add('is-focus');
        route.set.classList.add('is-focus');
      }
    });
    svg.appendChild(portLayer);
    layout(true);
  };

  const sampleFrames = (d: string, hideA: number, hideB: number, parks: number[]) => {
    if (!measure) return null;
    measure.setAttribute('d', d);
    const length = measure.getTotalLength();
    if (!length) return null;
    const count = Math.max(40, Math.min(160, Math.round(length / 7)));
    const points: DOMPoint[] = [];
    for (let index = 0; index <= count; index += 1) points.push(measure.getPointAtLength((length * index) / count));
    const times = [0];
    for (let index = 1; index <= count; index += 1) {
      const at = (length * (index - 0.5)) / count;
      times.push((times[index - 1] ?? 0) + (at > hideA && at < hideB ? 0.25 : 1));
    }
    const total = times[count] ?? 1;
    const keyframes: Keyframe[] = [];
    let previousAngle = 0;
    for (let index = 0; index <= count; index += 1) {
      const before = points[Math.max(0, index - 1)] as DOMPoint;
      const after = points[Math.min(count, index + 1)] as DOMPoint;
      const point = points[index] as DOMPoint;
      let angle = (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI;
      if (index) {
        while (angle - previousAngle > 180) angle -= 360;
        while (angle - previousAngle < -180) angle += 360;
      }
      previousAngle = angle;
      const at = (length * index) / count;
      keyframes.push({ offset: (times[index] ?? 0) / total, transform: `translate(${round1(point.x)}px,${round1(point.y)}px) rotate(${round1(angle)}deg)`, opacity: at > hideA + 3 && at < hideB - 3 ? 0 : 1 });
    }
    const parkIdx = parks.map((park) => Math.max(0, Math.min(count, Math.round((park / length) * count))));
    return { frames: keyframes, parkIdx };
  };
  const framesFor = (packetSet: PacketSet) => (packetSet.set && packetSet.set.classList.contains('is-winner') ? packetSet.framesOpen : packetSet.framesHide) ?? null;
  const makeAnimation = (packet: PacketSlot, keyframes: Keyframe[]) => {
    packet.anim = packet.el.animate(keyframes, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS });
  };
  const setPath = (packetSet: PacketSet) => {
    const motion = packetSet.geo;
    if (!motion || !measure) return;
    measure.setAttribute('d', motion.head);
    const lengthA = measure.getTotalLength();
    const d = `${motion.head} ${motion.rest}`;
    measure.setAttribute('d', d);
    const length = measure.getTotalLength();
    const lengthB = Math.max(0, length - lengthA - motion.w);
    if (!length) return;
    const parks = motion.parks.map((fraction) => (fraction >= 0 ? fraction * lengthA : lengthA + motion.w + -fraction * lengthB));
    const result = sampleFrames(d, lengthA, lengthA + motion.w, parks);
    if (!result) return;
    packetSet.framesHide = result.frames;
    packetSet.framesOpen = result.frames.map((frame) => ({ offset: frame.offset, transform: frame.transform }));
    packetSet.frames = framesFor(packetSet);
    packetSet.parkIdx = result.parkIdx;
    packetSet.packets.forEach((packet, index) => {
      const keyframes = packetSet.frames as Keyframe[];
      if (packet.anim) {
        const effect = packet.anim.effect as KeyframeEffect | null;
        try {
          effect?.setKeyframes(keyframes);
        } catch {
          const time = packet.anim.currentTime;
          packet.anim.cancel();
          packet.anim = null;
          makeAnimation(packet, keyframes);
          if (packet.anim && time !== null) (packet.anim as Animation).currentTime = time;
        }
      } else if (!running) {
        const park = keyframes[result.parkIdx[index % result.parkIdx.length] ?? 0];
        packet.el.style.transform = String(park?.transform ?? '');
      }
    });
  };
  const refreshFrames = (packetSet: PacketSet) => {
    if (!packetSet.framesHide) return;
    const wanted = framesFor(packetSet);
    if (!wanted || packetSet.frames === wanted) return;
    packetSet.frames = wanted;
    packetSet.packets.forEach((packet) => {
      try {
        (packet.anim?.effect as KeyframeEffect | null)?.setKeyframes(wanted);
      } catch {
        // An effect that cannot take new keyframes keeps its old ones until the next layout.
      }
    });
  };

  let lastKey = '';
  const geometry: { mobile: boolean; pc: Box | null; game: Box | null; dir: Box | null; list: Box | null; rPc: number; rGame: number; pcLabelTop: number; gameLabelTop: number; limitTop: number; limitBottom: number } = { mobile: false, pc: null, game: null, dir: null, list: null, rPc: 0, rGame: 0, pcLabelTop: 0, gameLabelTop: 0, limitTop: 0, limitBottom: 0 };
  const axis: { y: number | null; raf: number; done: (() => void) | null } = { y: null, raf: 0, done: null };
  const endDy = { pc: 0, game: 0 };
  const snap = { x: 0, y: 0 };
  let motionArmed = false;
  let motionQueue: PacketSet[] = [];
  let motionRaf = 0;
  let motionCallbacks: (() => void)[] = [];

  const targetAxisY = () => {
    const winner = byId(winnerId);
    return winner?.rect ? winner.rect.cy : (geometry.list?.cy ?? 0);
  };

  const layout = (force: boolean) => {
    if (!built || !mask || !maskBg) return;
    const stageRect = svg.getBoundingClientRect();
    if (!stageRect.width || !stageRect.height) return;
    const directRect = directEl.getBoundingClientRect();
    const wrapRect = wrapEl.getBoundingClientRect();
    let rects = routes.map((route) => box(route.el.getBoundingClientRect(), stageRect));
    const mobileNow = isMobile();
    const dpr = window.devicePixelRatio || 1;
    const unsnappedX = wrapRect.left - snap.x;
    const unsnappedY = wrapRect.top - snap.y;
    const nextX = Math.round(unsnappedX * dpr) / dpr - unsnappedX;
    const nextY = Math.round(unsnappedY * dpr) / dpr - unsnappedY;
    const ddx = nextX - snap.x;
    const ddy = nextY - snap.y;
    const resnap = Math.abs(ddx) > 0.005 || Math.abs(ddy) > 0.005;
    const firstRect = rects[0] as Box;
    const lastRect = rects[rects.length - 1] as Box;
    const secondRect = rects[1] as Box;
    let pcRect = box(pcIcon.getBoundingClientRect(), stageRect);
    let gameRect = box(gameIcon.getBoundingClientRect(), stageRect);
    let pcLabelRect = box(pcLabel.getBoundingClientRect(), stageRect);
    let gameLabelRect = box(gameLabel.getBoundingClientRect(), stageRect);
    if (!mobileNow) {
      pcRect = shiftBox(pcRect, -endDy.pc);
      gameRect = shiftBox(gameRect, -endDy.game);
      pcLabelRect = shiftBox(pcLabelRect, -endDy.pc);
      gameLabelRect = shiftBox(gameLabelRect, -endDy.game);
    }
    // The node boxes are in the key so a label that rewraps (webfont landing, PT/EN copy, geo place) re-anchors the ports.
    const key = [Math.round(stageRect.width), Math.round(stageRect.height), mobileNow ? 1 : 0, Math.round(firstRect.t - snap.y), Math.round(lastRect.b - snap.y), Math.round(secondRect.t - snap.y), Math.round(directRect.top - snap.y - stageRect.top), Math.round(pcRect.t), Math.round(pcRect.l), Math.round(gameRect.t), Math.round(gameRect.l), Math.round(pcLabelRect.h), Math.round(gameLabelRect.h)].join('|');
    if (!force && !resnap && key === lastKey) return;
    lastKey = key;
    const radiusPc = parseFloat(getComputedStyle(pcIcon).borderTopLeftRadius) || 0;
    const radiusGame = parseFloat(getComputedStyle(gameIcon).borderTopLeftRadius) || 0;
    if (!pcRect.w || !gameRect.w || rects.some((rect) => !rect.w || !rect.h)) return;
    let directBox = box(directRect, stageRect);
    let listBox: Box = { l: firstRect.l, t: firstRect.t, r: firstRect.r, b: lastRect.b, w: firstRect.w, h: lastRect.b - firstRect.t, cx: firstRect.cx, cy: (firstRect.t + lastRect.b) / 2 };
    if (resnap) {
      rects = rects.map((rect) => shiftXY(rect, ddx, ddy));
      directBox = shiftXY(directBox, ddx, ddy);
      listBox = shiftXY(listBox, ddx, ddy);
    }
    routes.forEach((route, index) => {
      route.rect = rects[index];
    });
    if (resnap) {
      snap.x = nextX;
      snap.y = nextY;
      wrapEl.style.transform = Math.abs(nextX) < 0.005 && Math.abs(nextY) < 0.005 ? '' : `translate(${Math.round(nextX * 1000) / 1000}px,${Math.round(nextY * 1000) / 1000}px)`;
    }
    Object.assign(geometry, { mobile: mobileNow, pc: pcRect, game: gameRect, dir: directBox, list: listBox, rPc: radiusPc, rGame: radiusGame, pcLabelTop: pcLabelRect.t, gameLabelTop: gameLabelRect.t, limitTop: stage.getBoundingClientRect().top - stageRect.top, limitBottom: stageRect.height });
    svg.setAttribute('viewBox', `0 0 ${stageRect.width} ${stageRect.height}`);
    [mask, maskBg].forEach((element) => {
      element.setAttribute('x', String(-MASK_PAD));
      element.setAttribute('y', String(-MASK_PAD));
      element.setAttribute('width', String(stageRect.width + 2 * MASK_PAD));
      element.setAttribute('height', String(stageRect.height + 2 * MASK_PAD));
    });
    const midGlide = !!axis.raf;
    if (midGlide) {
      cancelFrame(axis.raf);
      axis.raf = 0;
    }
    axis.y = geometry.mobile ? null : targetAxisY();
    drawWires();
    stage.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
    if (midGlide) settleGlide();
  };

  const computeGeometry = (axisY: number | null): StageGeometry | null => {
    const { pc: pcBox, game: gameBox, dir: dirBox } = geometry;
    if (!pcBox || !gameBox || !dirBox) return null;
    const { mobile } = geometry;
    let pc = pcBox;
    let game = gameBox;
    let dPc = 0;
    let dGame = 0;
    if (!mobile && axisY !== null) {
      dPc = clampNodeShift(axisY - pcBox.cy, geometry.pcLabelTop, pcBox.b, geometry.limitTop, geometry.limitBottom);
      dGame = clampNodeShift(axisY - gameBox.cy, geometry.gameLabelTop, gameBox.b, geometry.limitTop, geometry.limitBottom);
      pc = shiftBox(pcBox, dPc);
      game = shiftBox(gameBox, dGame);
    }
    const a = mobile ? { x: pc.cx, y: pc.b + 1 } : { x: pc.r + 1, y: pc.cy };
    const b = mobile ? { x: game.cx, y: game.t - 1 } : { x: game.l - 1, y: game.cy };
    const ra = mobile ? { x: pc.l - 1, y: pc.b - (geometry.rPc + 5) } : { x: pc.r + 1, y: pc.cy + sideOffset(pc, geometry.rPc) };
    const rb = mobile ? { x: game.r + 1, y: game.t + (geometry.rGame + 5) } : { x: game.l - 1, y: game.cy + sideOffset(game, geometry.rGame) };
    const result: StageGeometry = { mobile, dPc, dGame, a, b, ra, rb, routes: [], direct: { wire: '', head: '', tail: '', l: 0, r: 0, cy: 0, geo: { head: '', rest: '', w: 0, parks: [] } }, trunks: ['', ''] };
    if (mobile) {
      const firstCy = routes[0]?.rect?.cy ?? 0;
      const lastCy = routes[routes.length - 1]?.rect?.cy ?? 0;
      result.trunks = [`M${round1(a.x)},${round1(a.y)} L${round1(a.x)},${round1(lastCy - STUB_V)}`, `M${round1(b.x)},${round1(firstCy + STUB_V)} L${round1(b.x)},${round1(b.y)}`];
    }
    routes.forEach((route) => {
      const rect = route.rect as Box;
      let segmentB: string;
      let head: string;
      let base: string | null = null;
      if (mobile) {
        const sIn = stubIn(a.x, rect.cy, rect.l);
        const sOut = stubOut(rect.r, rect.cy, b.x);
        head = `M${round1(a.x)},${round1(a.y)} L${round1(a.x)},${round1(rect.cy - STUB_V)} ${sIn}`;
        segmentB = `${sOut} L${round1(b.x)},${round1(b.y)}`;
        base = `M${round1(a.x)},${round1(rect.cy - STUB_V)} ${sIn} M${round1(rect.r)},${round1(rect.cy)} ${sOut}`;
      } else {
        head = `M${round1(a.x)},${round1(a.y)} ${fanA(a.x, a.y, rect.l, rect.cy)}`;
        segmentB = fanB(rect.r, rect.cy, b.x, b.y);
      }
      const full = `${head} M${round1(rect.r)},${round1(rect.cy)} ${segmentB}`;
      result.routes.push({ base: base ?? full, full, l: rect.l, r: rect.r, cy: rect.cy, geo: { head, rest: `L${round1(rect.r)},${round1(rect.cy)} ${segmentB}`, w: rect.w, parks: [0.45, 0.85, -0.6] } });
    });
    const directA = mobile ? `L${round1(ra.x)},${round1(dirBox.cy - STUB_V)} ${stubIn(ra.x, dirBox.cy, dirBox.l)}` : fanA(ra.x, ra.y, dirBox.l, dirBox.cy);
    const directB = mobile ? `${stubOut(dirBox.r, dirBox.cy, rb.x)} L${round1(rb.x)},${round1(rb.y)}` : fanB(dirBox.r, dirBox.cy, rb.x, rb.y);
    const directHead = `M${round1(ra.x)},${round1(ra.y)} ${directA}`;
    result.direct = { wire: `${directHead} M${round1(dirBox.r)},${round1(dirBox.cy)} ${directB}`, head: directHead, tail: `M${round1(dirBox.r)},${round1(dirBox.cy)} ${directB}`, l: dirBox.l, r: dirBox.r, cy: dirBox.cy, geo: { head: directHead, rest: `L${round1(dirBox.r)},${round1(dirBox.cy)} ${directB}`, w: dirBox.w, parks: [0.55, -0.5] } };
    return result;
  };

  const fitDash = (path: SVGPathElement, d: string) => {
    path.setAttribute('d', d);
    const length = path.getTotalLength();
    const dash = 6;
    if (!length) return;
    const count = Math.max(1, Math.round((length - dash) / 13));
    path.setAttribute('stroke-dasharray', `${dash} ${round1(Math.max(3, (length - dash) / count - dash))}`);
  };
  const paintGeometry = (stageGeometry: StageGeometry) => {
    if (!stageGeometry.mobile) {
      pcNode.style.setProperty('--mc-dy', `${round1(stageGeometry.dPc)}px`);
      gameNode.style.setProperty('--mc-dy', `${round1(stageGeometry.dGame)}px`);
      endDy.pc = stageGeometry.dPc;
      endDy.game = stageGeometry.dGame;
    }
    ports.pc?.setAttribute('cx', String(round1(stageGeometry.a.x)));
    ports.pc?.setAttribute('cy', String(round1(stageGeometry.a.y)));
    ports.game?.setAttribute('cx', String(round1(stageGeometry.b.x)));
    ports.game?.setAttribute('cy', String(round1(stageGeometry.b.y)));
    trunks[0]?.setAttribute('d', stageGeometry.trunks[0]);
    trunks[1]?.setAttribute('d', stageGeometry.trunks[1]);
    routes.forEach((route, index) => {
      const wire = stageGeometry.routes[index];
      if (!wire) return;
      route.pathBase?.setAttribute('d', wire.base);
      [route.pathGlow, route.pathHot, route.pathSweep, route.pathKnock].forEach((path) => path?.setAttribute('d', wire.full));
      route.portIn?.setAttribute('cx', String(round1(wire.l)));
      route.portIn?.setAttribute('cy', String(round1(wire.cy)));
      route.portOut?.setAttribute('cx', String(round1(wire.r)));
      route.portOut?.setAttribute('cy', String(round1(wire.cy)));
    });
    const wire = stageGeometry.direct;
    if (directPath) fitDash(directPath, wire.head);
    if (directTail) fitDash(directTail, wire.tail);
    const coordinates: [number, number][] = [
      [wire.l, wire.cy],
      [wire.r, wire.cy],
      [stageGeometry.ra.x, stageGeometry.ra.y],
      [stageGeometry.rb.x, stageGeometry.rb.y],
    ];
    coordinates.forEach(([x, y], index) => {
      directPorts[index]?.setAttribute('cx', String(round1(x)));
      directPorts[index]?.setAttribute('cy', String(round1(y)));
    });
  };
  const setMotionGeometry = (stageGeometry: StageGeometry) => {
    routes.forEach((route, index) => {
      route.geo = stageGeometry.routes[index]?.geo ?? null;
    });
    direct.geo = stageGeometry.direct.geo;
  };
  const drawWires = () => {
    const stageGeometry = computeGeometry(axis.y);
    if (!stageGeometry) return;
    paintGeometry(stageGeometry);
    setMotionGeometry(stageGeometry);
  };
  const settleGlide = () => {
    if (axis.raf) {
      cancelFrame(axis.raf);
      axis.raf = 0;
    }
    const { done } = axis;
    axis.done = null;
    done?.();
  };
  const glideAxis = (duration: number, done?: () => void) => {
    settleGlide();
    if (!geometry.pc || geometry.mobile) {
      done?.();
      return;
    }
    const to = targetAxisY();
    const from = axis.y;
    if (from === null || reduced.matches || !duration || Math.abs(to - from) < 0.5) {
      axis.y = to;
      drawWires();
      done?.();
      return;
    }
    const startedAt = performance.now();
    axis.done = done ?? null;
    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      axis.y = from + (to - from) * springOut(progress);
      drawWires();
      if (progress < 1) {
        axis.raf = nextFrame(step);
        return;
      }
      axis.raf = 0;
      axis.y = to;
      drawWires();
      settleGlide();
    };
    axis.raf = nextFrame(step);
  };
  const reaim = (result: WinnerResult, callback?: () => void) => {
    const moves = !geometry.mobile && result.changed && !!geometry.pc;
    const ignite = () => {
      stage.classList.remove('is-aiming');
      if (!reduced.matches) restartClass(result.best.el, 'is-burst');
      callback?.();
    };
    if (!moves) {
      ignite();
      return;
    }
    stage.classList.add('is-aiming');
    const go = () => {
      if (motionArmed) {
        const target = computeGeometry(targetAxisY());
        if (target) setMotionGeometry(target);
        scheduleMotion(() => glideAxis(720, ignite));
      } else glideAxis(720, ignite);
    };
    if (reduced.matches) go();
    else later(go, 180);
  };
  const scheduleMotion = (callback?: () => void) => {
    if (!built) {
      callback?.();
      return;
    }
    motionQueue = [...routes, direct];
    if (callback) motionCallbacks.push(callback);
    if (motionRaf) cancelFrame(motionRaf);
    motionRaf = nextFrame(motionStep);
  };
  const motionStep = () => {
    const next = motionQueue.shift();
    if (next) setPath(next);
    if (motionQueue.length) {
      motionRaf = nextFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
    const callbacks = motionCallbacks;
    motionCallbacks = [];
    callbacks.forEach((callback) => callback());
  };
  const armMotion = () => {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage.classList.contains('is-ready')) scheduleMotion();
  };
  const rateFor = (lat: number) => (BASE_MS / (700 + lat * 14)) * boost;
  const ensureAnimations = (packetSet: PacketSet) => {
    if (!packetSet.frames) return;
    packetSet.packets.forEach((packet) => {
      if (packet.anim) return;
      packet.el.style.transform = '';
      makeAnimation(packet, packetSet.frames as Keyframe[]);
    });
  };
  const setRates = () => {
    routes.forEach((route) => {
      route.packets.forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rateFor(route.lat);
      });
    });
    direct.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = rateFor(direct.lat) * 0.8;
    });
  };
  const eachAnimation = (callback: (animation: Animation) => void) => {
    [...routes, direct].forEach((packetSet) => {
      packetSet.packets.forEach((packet) => {
        if (packet.anim) callback(packet.anim);
      });
    });
  };
  const start = () => {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    routes.forEach(ensureAnimations);
    ensureAnimations(direct);
    setRates();
    eachAnimation((animation) => {
      if (animation.playState !== 'running') animation.play();
    });
  };
  const stop = () => {
    running = false;
    root.classList.remove('is-live');
    eachAnimation((animation) => animation.pause());
  };
  const drawStatic = () => {
    running = false;
    root.classList.remove('is-live');
    [...routes, direct].forEach((packetSet) => {
      if (!packetSet.frames) return;
      packetSet.packets.forEach((packet, index) => {
        if (packet.anim) {
          packet.anim.cancel();
          packet.anim = null;
        }
        const parkIndex = packetSet.parkIdx ?? [0];
        const park = packetSet.frames?.[parkIndex[index % parkIndex.length] ?? 0];
        packet.el.style.transform = String(park?.transform ?? '');
      });
    });
  };

  /* ---------------------------------------------------------------- winner */
  const applyWinner = (burst: boolean): WinnerResult => {
    const best = [...routes].sort((first, second) => first.lat - second.lat)[0] as Route;
    const prev = byId(winnerId);
    const changed = prev !== null && best.id !== prev.id;
    winnerId = best.id;
    routes.forEach((route) => {
      const win = route.id === winnerId;
      route.el.classList.toggle('is-winner', win);
      if (route.g && route.set) {
        route.g.classList.toggle('is-winner', win);
        route.set.classList.toggle('is-winner', win);
        refreshFrames(route);
      }
      route.el.classList.remove('is-burst');
      route.el.setAttribute('aria-label', labelFor(route, best));
    });
    if (built) {
      ports.pc?.classList.add('is-hot');
      ports.game?.classList.add('is-hot');
    }
    if (burst && !reduced.matches) restartClass(best.el, 'is-burst');
    directEl.setAttribute('aria-label', t('direct_label', { ms: badMs - best.lat }));
    const badge = directMs.textContent;
    syncDerived();
    if (built && directMs.textContent !== badge && directMs.parentElement) flip(directMs.parentElement);
    setProof(best.lat);
    return { best, prev, changed };
  };
  const jitter = (value: number, amplitude: number) => Math.max(LAT_MIN, value + Math.round((Math.random() * 2 - 1) * Math.min(1, amplitude)));
  const applyScenario = (scenario: Scenario | undefined, amplitude: number, burst: boolean) => {
    setBad(scenario?.bad ?? badMs, amplitude);
    routes.forEach((route) => {
      route.lat = jitter(scenarioValue(scenario, route.id), amplitude);
      pushReading(route);
    });
    const result = applyWinner(false);
    routes.forEach((route) => {
      if (route.shown !== route.lat) tickMs(route, route.shown, route.lat);
    });
    if (running) setRates();
    if (result.changed) reaim(result);
    else if (burst && !reduced.matches) restartClass(result.best.el, 'is-burst');
    return result;
  };
  let scrambleTimer = 0;
  const uncrown = () => {
    routes.forEach((route) => {
      route.el.classList.remove('is-winner', 'is-burst');
      if (route.g && route.set) {
        route.g.classList.remove('is-winner');
        route.set.classList.remove('is-winner');
        refreshFrames(route);
      }
    });
  };
  const scramble = (on: boolean) => {
    clearEvery(scrambleTimer);
    scrambleTimer = 0;
    routes.forEach((route) => {
      if (route.tw) {
        cancelFrame(route.tw);
        route.tw = 0;
      }
    });
    if (!on) return;
    if (reduced.matches) {
      routes.forEach((route) => {
        route.msEl.textContent = '··';
      });
      return;
    }
    const tick = () =>
      routes.forEach((route) => {
        route.msEl.textContent = String(2 + Math.floor(Math.random() * 40));
      });
    tick();
    scrambleTimer = every(tick, 90);
  };
  const revealScenario = (scenario: Scenario | undefined, amplitude: number, done: () => void) => {
    setBad(scenario?.bad ?? badMs, 0);
    routes.forEach((route) => {
      route.lat = jitter(scenarioValue(scenario, route.id), amplitude);
      route.msEl.textContent = '··';
    });
    if (running) setRates();
    const finish = () => {
      routes.forEach((route) => route.msWrap.classList.remove('is-set'));
      done();
    };
    if (reduced.matches) {
      routes.forEach((route) => {
        setShown(route, route.lat);
        pushReading(route);
      });
      finish();
      return;
    }
    routes.forEach((route, index) => {
      later(
        () => {
          setShown(route, route.lat);
          pushReading(route);
          route.msWrap.classList.add('is-set');
          flip(route.msWrap);
        },
        40 + index * 60
      );
    });
    later(finish, 40 + routes.length * 60 + 160);
  };

  /* ---------------------------------------------------------------- status */
  const statusFor = (result: WinnerResult, context: string) => {
    const { best } = result;
    if (result.changed && result.prev) return t('status.changed', { prev: bold(result.prev.city), prevMs: stMs(result.prev), best: bold(best.city), bestMs: stMs(best) });
    if (context === 'measured') return t('status.measured', { best: bold(best.city), ms: stMs(best) });
    return t('status.leading', { best: bold(best.city), ms: stMs(best) });
  };
  const swapStatus = (html: string, fade: boolean) => {
    if (statusText.innerHTML === html) return;
    statusText.classList.remove('is-swap');
    statusText.innerHTML = html;
    if (fade && !reduced.matches) restartClass(statusText, 'is-swap');
  };
  const describeFocus = () => {
    const route = byId(focusId);
    const best = byId(winnerId);
    if (!route || !best) return;
    if (route.id === winnerId) {
      statusText.innerHTML = t('status.focus_winner', { city: bold(route.city), ms: stMs(route) });
      return;
    }
    statusText.innerHTML = t(rankOf(route) === 2 ? 'status.focus_next' : 'status.focus_reserve', { city: bold(route.city), delta: stDelta(route, best), best: escapeHtml(best.city) });
  };
  const announce = (result: WinnerResult, context: string) => {
    if (focusId) {
      describeFocus();
      return;
    }
    swapStatus(paused ? t('status.paused', { best: bold(result.best.city), ms: stMs(result.best) }) : statusFor(result, context), context === 'swap' && result.changed);
  };
  const live = (message: string) => {
    liveEl.textContent = '';
    liveEl.textContent = message;
  };
  const engaged = () => !!(pinned || focusId || list.contains(document.activeElement));

  const retest = (fromUser: boolean) => {
    if (testing) return;
    testing = true;
    scenarioIndex = (scenarioIndex + 1) % scenarios.length;
    stage.classList.add('is-testing');
    proofEl.classList.add('is-measuring');
    uncrown();
    scramble(true);
    routes.forEach((route) => route.el.setAttribute('aria-label', t('route_label_measuring', { city: route.city, country: route.country })));
    directEl.setAttribute('aria-label', t('direct_label_measuring'));
    retestButton.classList.add('is-testing');
    retestButton.setAttribute('aria-disabled', 'true');
    retestButton.setAttribute('aria-busy', 'true');
    retestLabel.textContent = t('retest_busy');
    statusText.textContent = t('status.measuring');
    if (fromUser) live(t('live.measuring'));
    boost = 2.4;
    if (running) setRates();
    later(
      () => {
        scramble(false);
        revealScenario(scenarios[scenarioIndex], 1, () => {
          stage.classList.remove('is-testing');
          const result = applyWinner(false);
          proofEl.classList.remove('is-measuring');
          retestButton.classList.remove('is-testing');
          retestButton.removeAttribute('aria-disabled');
          retestButton.setAttribute('aria-busy', 'false');
          retestLabel.textContent = t('retest');
          boost = 1;
          if (running) setRates();
          testing = false;
          reaim(result);
          announce(result, fromUser ? 'measured' : '');
          if (fromUser) {
            bridge.classList.add('is-hot');
            const handover = result.changed && result.prev ? t('live.handover', { prev: result.prev.city, best: result.best.city }) : '';
            live(t('live.measured', { city: result.best.city, ms: result.best.lat, handover, pct: pctFor(result.best.lat) }));
          }
        });
      },
      reduced.matches ? 80 : 900
    );
  };

  /* the auto demo: a scenario swap every other beat, a gentle re-read in between */
  let loopCount = 0;
  const canAnimate = () => built && visible && !document.hidden && !reduced.matches && !testing && !paused;
  const loop = () => {
    later(() => {
      if (canAnimate() && !engaged()) {
        loopCount += 1;
        if (loopCount % 2 === 0) {
          scenarioIndex = (scenarioIndex + 1) % scenarios.length;
          announce(applyScenario(scenarios[scenarioIndex], 1, true), 'swap');
        } else announce(applyScenario(scenarios[scenarioIndex], 3, false), '');
      }
      loop();
    }, LOOP_MS);
  };
  /* live readings: every half second the figures move within their bands without changing who leads */
  const livePing = () => {
    later(() => {
      if (canAnimate() && winnerId) {
        const winner = byId(winnerId) as Route;
        const scenario = scenarios[scenarioIndex];
        winner.lat = randomInt(2, 5);
        routes.forEach((route) => {
          if (route !== winner) route.lat = Math.max(winner.lat + 1, jitter(scenarioValue(scenario, route.id), 1));
          if (route.tw) {
            cancelFrame(route.tw);
            route.tw = 0;
          }
          setShown(route, route.lat);
          pushReading(route);
        });
        setBad(randomInt(BAD_LO, BAD_HI), 0);
        syncDerived();
        setProof(winner.lat);
      }
      livePing();
    }, LIVE_MS);
  };

  /* ------------------------------------------------------------ interaction */
  let hintGone = false;
  const dismissHint = () => {
    if (hintGone) return;
    hintGone = true;
    if (reduced.matches) {
      hintText.hidden = true;
      return;
    }
    hintText.classList.add('is-gone');
    later(() => {
      hintText.hidden = true;
    }, 450);
  };
  const setFocus = (id: string | null, sticky?: boolean) => {
    if (id) dismissHint();
    focusId = id;
    stage.classList.toggle('has-focus', !!id);
    routes.forEach((route) => {
      const on = route.id === id;
      route.el.classList.toggle('is-focus', on);
      if (route.g && route.set) {
        route.g.classList.toggle('is-focus', on);
        route.set.classList.toggle('is-focus', on);
      }
      if (sticky !== undefined) route.el.setAttribute('aria-pressed', on && sticky ? 'true' : 'false');
    });
    if (id) describeFocus();
    else {
      const winner = byId(winnerId);
      if (winner) announce({ best: winner, prev: null, changed: false }, '');
    }
  };
  const setPin = (id: string | null) => {
    pinned = id;
    routes.forEach((route) => route.el.classList.toggle('is-pinned', route.id === id));
    root.classList.toggle('has-pin', !!id);
    if (id) {
      hintGone = true;
      hintText.classList.add('is-gone');
      hintText.hidden = true;
    }
    clearButton.hidden = !id;
  };
  const clearPin = (returnFocusTo?: HTMLElement | null) => {
    const had = !!(pinned || focusId);
    setPin(null);
    setFocus(null, false);
    if (had) live(t('live.cleared'));
    returnFocusTo?.focus({ preventScroll: true });
  };
  const routeButton = (event: Event) => (event.target instanceof Element ? event.target.closest<HTMLButtonElement>('[data-route]') : null);

  listen(list, 'pointerover', (event) => {
    const button = routeButton(event);
    if (!button || pinned || event.pointerType === 'touch') return;
    setFocus(button.getAttribute('data-route'));
  });
  listen(list, 'pointerout', (event) => {
    const button = routeButton(event);
    if (!button || pinned || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    setFocus(null);
  });
  listen(list, 'click', (event) => {
    const button = routeButton(event);
    if (!button) return;
    const id = button.getAttribute('data-route');
    if (pinned === id) {
      clearPin();
      return;
    }
    setPin(id);
    setFocus(id, true);
    if (event.detail === 0) clearButton.focus({ preventScroll: true });
    const route = byId(id);
    const winner = byId(winnerId);
    if (!route || !winner) return;
    live(id === winnerId ? t('live.pinned_winner', { city: route.city, ms: route.lat }) : t('live.pinned', { city: route.city, ms: route.lat, delta: route.lat - winner.lat, best: winner.city }));
  });
  listen(list, 'focusin', (event) => {
    const button = routeButton(event);
    if (!button || pinned) return;
    setFocus(button.getAttribute('data-route'));
  });
  listen(list, 'focusout', (event) => {
    if (pinned) return;
    if (!(event.relatedTarget instanceof Node) || !list.contains(event.relatedTarget)) setFocus(null);
  });
  listen(stage, 'click', (event) => {
    if (pinned && !(event.target instanceof Element && event.target.closest('[data-route]'))) clearPin();
  });
  listen(clearButton, 'click', (event) => {
    const target = pinned && event.detail === 0 ? byId(pinned)?.el : null;
    clearPin(target);
  });
  listen(root, 'keydown', (event) => {
    if (event.key === 'Escape' && (pinned || focusId)) clearPin();
  });
  listen(retestButton, 'click', () => {
    if (retestButton.getAttribute('aria-disabled') === 'true') return;
    retest(true);
  });
  listen(pauseButton, 'click', () => {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (paused) stop();
    else syncRun();
    const winner = byId(winnerId);
    if (winner) announce({ best: winner, prev: null, changed: false }, '');
    live(paused ? t('live.paused') : t('live.resumed'));
  });

  /* the offer button carries [data-np-trial]: React's own handler opens the enrolment dialog */
  const syncRun = () => {
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
    } else stop();
  };

  if (typeof IntersectionObserver === 'function') {
    const buildObserver = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    buildObserver.observe(stage);
    const visibleObserver = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    visibleObserver.observe(stage);
    runtime.onDispose(() => {
      buildObserver.disconnect();
      visibleObserver.disconnect();
    });
    later(() => {
      if (built || document.hidden) return;
      const rect = stage.getBoundingClientRect();
      if (rect.bottom > -320 && rect.top < window.innerHeight + 320) buildStage();
    }, 1500);
  } else {
    visible = true;
    armMotion();
  }
  const onVisibility = () => syncRun();
  document.addEventListener('visibilitychange', onVisibility);
  runtime.onDispose(() => document.removeEventListener('visibilitychange', onVisibility));
  let resizeRaf = 0;
  const scheduleLayout = () => {
    if (resizeRaf) cancelFrame(resizeRaf);
    resizeRaf = nextFrame(() => layout(false));
  };
  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleLayout);
    resizeObserver.observe(stage);
    runtime.onDispose(() => resizeObserver.disconnect());
  } else {
    window.addEventListener('resize', scheduleLayout);
    runtime.onDispose(() => window.removeEventListener('resize', scheduleLayout));
  }
  document.fonts?.ready.then(() => {
    if (alive()) scheduleLayout();
  });
  const onReducedChange = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  reduced.addEventListener('change', onReducedChange);
  runtime.onDispose(() => reduced.removeEventListener('change', onReducedChange));

  /* one geolocation answer per page, shared with Multi Internet */
  const relocate = (answer: ReturnType<typeof normalizeGeo>) => {
    const profile = profileFor(answer);
    if (profile.key === place.key) return;
    applyProfile(profile);
    publishPlace();
    scenarioIndex = 0;
    routes.forEach((route) => {
      route.lat = scenarioValue(scenarios[0], route.id);
      setShown(route, route.lat);
      paintRow(route);
      seedHistory(route, 1);
      drawSpark(route);
    });
    paintPlace();
    setBad(badMs, 0);
    const result = applyWinner(false);
    if (running) setRates();
    if (built) reaim(result);
    announce(result, '');
  };
  if (!forced) {
    requestVisitorGeo().then((answer) => {
      const normalized = normalizeGeo(answer);
      if (alive() && normalized) relocate(normalized);
    });
  }

  announce(applyWinner(false), '');
  loop();
  livePing();

  return () => {
    runtime.dispose();
    eachAnimation((animation) => animation.cancel());
    packetLayer.replaceChildren();
    svg.replaceChildren();
    root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'has-pin');
  };
}
